import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, Share2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { slugifyTerm } from '@/lib/utils';
import { legalTerms } from '@/data/legalTerms';
import SEO from '@/components/SEO';
import { toast } from '@/hooks/use-toast';
const DictionaryTerm = () => {
  const { term } = useParams<{ term: string }>();
  const navigate = useNavigate();
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dictionary');
    }
  };

  // Use shared legal terms dataset from src/data/legalTerms.ts

  const param = (term ?? '').toLowerCase();
  const currentTerm = legalTerms.find(t => {
    const slug = slugifyTerm(t.term);
    return slug === param || t.term.toLowerCase() === param;
  });

  const slug = slugifyTerm(currentTerm.term);
  const shareUrl = `${window.location.origin}/share/dictionary/${slug}.html`;
  const onShare = async () => {
    try {
      const shareData = {
        title: `${currentTerm.term} definition | Legal Dictionary`,
        text: currentTerm.definition,
        url: shareUrl,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast({ title: 'Link copied', description: 'Share link copied to clipboard.' });
      }
    } catch (e) {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copied', description: 'Share link copied to clipboard.' });
    }
  };

  // SEO handled by reusable <SEO /> component
  if (!currentTerm) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="min-h-screen pt-24 pb-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Term Not Found
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                The requested legal term could not be found.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Link helper for occurrences of "United States" within definitions
  const unitedStatesSlug = slugifyTerm('UNITED STATES');
  const renderUnitedStatesLinks = (text: string) => {
    const parts = text.split(/(United States)/g);
    let occurrence = 0;
    return parts.map((part, idx) => {
      if (part === 'United States') {
        occurrence += 1;
        if (occurrence === 1) {
          // Do not link the very first occurrence (e.g., in the opening quoted term)
          return part;
        }
        return (
          <Link
            key={idx}
            to={`/dictionary/${unitedStatesSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of UNITED STATES"
          >
            United States
          </Link>
        );
      }
      return part;
    });
  };
  // Link helper for occurrences of "litigation" within definitions (case-insensitive)
  function renderLitigationLinks(text: string) {
    const litigationSlug = slugifyTerm('Litigation');
    const parts = text.split(/(litigation)/gi);
    return parts.map((part, idx) =>
      part.toLowerCase() === 'litigation' ? (
        <Link
          key={idx}
          to={`/dictionary/${litigationSlug}`}
          className="underline underline-offset-4 text-link"
          aria-label="View definition of Litigation"
        >
          {part}
        </Link>
      ) : (
        part
      )
    );
  }

  // Link helper for 'apply for Federal Reserve Notes' definition
  function renderApplyFRNLinks(text: string) {
    const noteSlug = slugifyTerm('Note/instrument');
    const draftSlug = slugifyTerm('draft');
    const usSlug = slugifyTerm('UNITED STATES');
    const parts = text.split(/(United States|[Nn]otes|drafts)/g);
    return parts.map((part, idx) => {
      if (part.toLowerCase() === 'notes') {
        const prev = parts[idx - 1] ?? '';
        const isFRNPhrase = /Federal Reserve\s*$/i.test(prev);
        if (isFRNPhrase) {
          // Do not link when part of the phrase "Federal Reserve notes"
          return part;
        }
        return (
          <Link
            key={idx}
            to={`/dictionary/${noteSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of Note/instrument"
          >
            {part}
          </Link>
        );
      }
      if (part === 'drafts') {
        return (
          <Link
            key={idx}
            to={`/dictionary/${draftSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of draft"
          >
            drafts
          </Link>
        );
      }
      if (part === 'United States') {
        return (
          <Link
            key={idx}
            to={`/dictionary/${usSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of UNITED STATES"
          >
            United States
          </Link>
        );
      }
      return part;
    });
  }

  // Link helper for Bill of Exchange: link "negotiable instrument" and "draft"
  function renderBillOfExchangeLinks(text: string) {
    const niSlug = slugifyTerm('Negotiable instrument');
    const draftSlug = slugifyTerm('draft');
    const parts = text.split(/(negotiable instrument|draft)/gi);
    return parts.map((part, idx) => {
      const lower = part.toLowerCase();
      if (lower === 'negotiable instrument') {
        return (
          <Link
            key={idx}
            to={`/dictionary/${niSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of Negotiable instrument"
          >
            {part}
          </Link>
        );
      }
      if (lower === 'draft') {
        return (
          <Link
            key={idx}
            to={`/dictionary/${draftSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of draft"
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  }

  // Link helper for attorney-in-fact: link "power of attorney"
  function renderAttorneyInFactLinks(text: string) {
    const powerOfAttorneySlug = slugifyTerm('power of attorney');
    const parts = text.split(/(power of attorney)/gi);
    return parts.map((part, idx) =>
      part.toLowerCase() === 'power of attorney' ? (
        <Link
          key={idx}
          to={`/dictionary/${powerOfAttorneySlug}`}
          className="underline underline-offset-4 text-link"
          aria-label="View definition of power of attorney"
        >
          {part}
        </Link>
      ) : (
        part
      )
    );
  }

  // Link helper for power of attorney: link "attorney-in-fact"
  function renderPowerOfAttorneyLinks(text: string) {
    const attorneyInFactSlug = slugifyTerm('attorney-in-fact');
    const parts = text.split(/(attorney-in-fact|attorneys-in-fact)/gi);
    return parts.map((part, idx) => {
      const lower = part.toLowerCase();
      if (lower === 'attorney-in-fact' || lower === 'attorneys-in-fact') {
        return (
          <Link
            key={idx}
            to={`/dictionary/${attorneyInFactSlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of attorney-in-fact"
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  }

  // Link helper for durable power of attorney: link "power of attorney" and "attorney-in-fact"
  function renderDurablePowerOfAttorneyLinks(text: string) {
    const powerOfAttorneySlug = slugifyTerm('power of attorney');
    const attorneyInFactSlug = slugifyTerm('attorney-in-fact');
    
    // First replace power of attorney links
    let parts = text.split(/(power of attorney)/gi);
    let result: (string | React.ReactElement)[] = [];
    
    parts.forEach((part, idx) => {
      if (part.toLowerCase() === 'power of attorney') {
        result.push(
          <Link
            key={`poa-${idx}`}
            to={`/dictionary/${powerOfAttorneySlug}`}
            className="underline underline-offset-4 text-link"
            aria-label="View definition of power of attorney"
          >
            {part}
          </Link>
        );
      } else {
        result.push(part);
      }
    });
    
    // Then handle attorney-in-fact links in the combined result
    const finalResult: (string | React.ReactElement)[] = [];
    result.forEach((item, idx) => {
      if (typeof item === 'string') {
        const attorneyParts = item.split(/(attorney-in-fact)/gi);
        attorneyParts.forEach((attorneyPart, attorneyIdx) => {
          if (attorneyPart.toLowerCase() === 'attorney-in-fact') {
            finalResult.push(
              <Link
                key={`aif-${idx}-${attorneyIdx}`}
                to={`/dictionary/${attorneyInFactSlug}`}
                className="underline underline-offset-4 text-link"
                aria-label="View definition of attorney-in-fact"
              >
                {attorneyPart}
              </Link>
            );
          } else {
            finalResult.push(attorneyPart);
          }
        });
      } else {
        finalResult.push(item);
      }
    });
    
    return finalResult;
  }

  // Link helper for private person: link "United States" to external definition
  function renderPrivatePersonLinks(text: string) {
    const parts = text.split(/(United States)/g);
    return parts.map((part, idx) => {
      if (part === 'United States') {
        return (
          <a
            key={idx}
            href="https://www.law.cornell.edu/definitions/uscode.php?width=840&height=800&iframe=true&def_id=19-USC-2032517217-1615069972&term_occur=999&term_src=title:19:chapter:13:subchapter:II:part:D:section:2571"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 text-link"
            aria-label="View definition of United States in geographical context"
          >
            United States
          </a>
        );
      }
      return part;
    });
  }

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: currentTerm.term,
    description: currentTerm.definition,
    url: window.location.origin + `/dictionary/${slugifyTerm(currentTerm.term)}`,
    inDefinedTermSet: window.location.origin + "/dictionary",
  };
  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title={`${currentTerm.term} definition | Legal Dictionary`}
        description={currentTerm.definition.length > 150 ? currentTerm.definition.slice(0, 157) + '...' : currentTerm.definition}
        image={'/lovable-uploads/05910b5a-16d6-43be-a86c-ee19a8a35c77.png'}
        url={window.location.origin + `/dictionary/${slugifyTerm(currentTerm.term)}`}
        type="article"
      />
      <div className="min-h-screen pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Share Button for UNITED STATES */}
            {slug === 'united-states' && (
              <div className="mb-8 flex items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={onShare}
                  aria-label={`Share ${currentTerm.term} definition`}
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
              </div>
            )}

            {/* Term Details */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
            />
            <Card className="mb-8">
              <CardHeader>
                <h1 className="text-[25.5px] md:text-[30.6px] font-bold">{currentTerm.term}</h1>
                <CardDescription className="text-lg">
                  {currentTerm.term.toLowerCase() === 'naturalization' ? (
                    <>
                      The term "naturalization" means the conferring of nationality of a state upon a{" "}
                      <Link to="/dictionary/person" className="underline underline-offset-4 text-link" aria-label="View definition of Person">person</Link>
                      {" "}after birth, by any means whatsoever.
                    </>
                  ) : currentTerm.term.toLowerCase() === 'united states citizen' ? (
                    renderUnitedStatesLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'without recourse' ? (
                    renderLitigationLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'apply for federal reserve notes' ? (
                    renderApplyFRNLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'bill of exchange' ? (
                    renderBillOfExchangeLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'attorney-in-fact' ? (
                    renderAttorneyInFactLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'power of attorney' ? (
                    renderPowerOfAttorneyLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'durable power of attorney' ? (
                    renderDurablePowerOfAttorneyLinks(currentTerm.definition)
                  ) : currentTerm.term.toLowerCase() === 'private person' ? (
                    renderPrivatePersonLinks(currentTerm.definition)
                  ) : (
                    currentTerm.definition
                  )}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Supporting Sources */}
            <div className="mb-8">
              <h3 className="text-[21.6px] font-semibold mb-6">Supporting Sources</h3>
              {currentTerm.supportingSources && currentTerm.supportingSources.length > 0 ? (
                <ul className="space-y-3">
                  {currentTerm.supportingSources.map((src, i) => (
                    <li key={i}>
                      {src.url ? (
                        <a
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-4 text-link"
                          aria-label={`Open supporting source: ${src.title}`}
                        >
                          {src.title}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">{src.title}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground">No supporting sources have been added yet.</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Images */}
            {currentTerm.images && currentTerm.images.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[21.6px] font-semibold mb-6">Images</h3>
                <ul className="space-y-3">
                  {currentTerm.images.map((img, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => setImagePreview(img.url)}
                        className="underline underline-offset-4 text-link"
                        aria-label={`Open image: ${img.title}`}
                      >
                        {img.title}
                      </button>
                    </li>
                  ))}
                </ul>
                <Dialog open={!!imagePreview} onOpenChange={(open) => !open && setImagePreview(null)}>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>{currentTerm.term} image</DialogTitle>
                    </DialogHeader>
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt={`${currentTerm.term} image`}
                        loading="lazy"
                        className="w-full h-auto rounded-md"
                      />
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DictionaryTerm;